import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AuthCallback } from '@/components/AuthCallback';
import { Sidebar } from '@/components/Sidebar';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { Toaster } from 'sonner';
import { Menu, X } from 'lucide-react';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import CASALibraryPage from '@/pages/CASALibraryPage';
import CompanyDocsPage from '@/pages/CompanyDocsPage';
import OperationsPage from '@/pages/OperationsPage';
import ProjectsPage from '@/pages/ProjectsPage';
import CalendarPage from '@/pages/CalendarPage';
import MedicalCentrePage from '@/pages/MedicalCentrePage';
import UserManagementPage from '@/pages/UserManagementPage';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 border-2 border-[#0ea5e9] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-mono text-xs tracking-wider uppercase">Loading</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-[#020617] relative">
      {/* Video background */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute min-w-full min-h-full object-cover opacity-15"
          style={{ filter: 'brightness(0.6)' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_657df0ad-2496-4c2a-b42e-93cd0609284a/artifacts/b5se8ucw_b6de3232-5867-4ad3-adef-fc8fda6a7dd6.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay for better readability */}
        <div className="absolute inset-0 bg-[#020617]/70" />
      </div>
      
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 md:hidden bg-slate-900 border border-slate-700 rounded-lg p-2 text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
      >
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - responsive */}
      <div className={`
        fixed md:relative z-40
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </div>

      {/* Main content - responsive margin */}
      <main className="flex-1 md:ml-[250px] min-h-screen overflow-auto relative z-10 pt-16 md:pt-0">
        {children}
      </main>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout><DashboardPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/casa-library" element={
        <ProtectedRoute>
          <AppLayout><CASALibraryPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/company-docs" element={
        <ProtectedRoute>
          <AppLayout><CompanyDocsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/operations" element={
        <ProtectedRoute>
          <AppLayout><OperationsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute>
          <AppLayout><ProjectsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/medical" element={
        <ProtectedRoute>
          <AppLayout><MedicalCentrePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <AppLayout><CalendarPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          <AppLayout><UserManagementPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: '#0f172a',
              border: '1px solid #1e293b',
              color: '#e2e8f0',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
